import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import Header from '@/components/Header';
import MobileHeader from '@/components/mobile/MobileHeader';
import MobileBottomNav from '@/components/mobile/MobileBottomNav';
import Footer from '@/components/Footer';
import { 
  Users, 
  Shield, 
  BarChart3, 
  Settings, 
  AlertTriangle, 
  CheckCircle,
  XCircle,
  Clock,
  TrendingUp,
  Building,
  Calendar,
  DollarSign
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useAuthenticatedUser } from '@/hooks/useAuthenticatedUser';
import { ComplianceDashboard } from '@/components/admin/ComplianceDashboard';
import { PolicyManagementAdmin } from '@/components/admin/PolicyManagementAdmin';
import { SeasonRolloverWizard } from '@/components/admin/SeasonRolloverWizard';
import { ScheduledRolloverManager } from '@/components/admin/ScheduledRolloverManager';
import { SeasonArchiveViewer } from '@/components/seasons/SeasonArchiveViewer';

interface SystemStats {
  totalUsers: number;
  activeGames: number;
  pendingApprovals: number;
  totalVenues: number;
  recentActivity: ActivityItem[];
  totalRevenue: number;
  pendingTransactions: number;
  refundCount: number;
}

interface ActivityItem {
  id: string;
  type: 'user_registration' | 'game_created' | 'venue_claimed' | 'approval_request';
  description: string;
  timestamp: string;
  status: 'completed' | 'pending' | 'failed';
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState<SystemStats>({
    totalUsers: 0,
    activeGames: 0,
    pendingApprovals: 0,
    totalVenues: 0,
    recentActivity: [],
    totalRevenue: 0,
    pendingTransactions: 0,
    refundCount: 0
  });
  const [loading, setLoading] = useState(true);
  const { user } = useAuthenticatedUser();

  useEffect(() => {
    if (user?.id) {
      loadSystemStats();
    }
  }, [user]);

  const loadSystemStats = async () => {
    try {
      // Get user count
      const { count: userCount } = await supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true });

      // Get active games count
      const { count: gameCount } = await supabase
        .from('venue_games')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'approved')
        .gte('game_date', new Date().toISOString().split('T')[0]);

      // Get pending approvals count
      const { count: approvalCount } = await supabase
        .from('approval_requests')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'pending');

      // Get venue count
      const { count: venueCount } = await supabase
        .from('venues')
        .select('*', { count: 'exact', head: true });

      // Get transaction stats
      const { data: transactions } = await (supabase as any)
        .from('transactions')
        .select('amount, status, transaction_type');

      let totalRevenue = 0;
      let pendingTransactions = 0;
      let refundCount = 0;

      if (transactions) {
        transactions.forEach((tx: any) => {
          const amount = parseFloat(tx.amount) || 0;
          if (tx.status === 'completed') {
            totalRevenue += amount;
          }
          if (tx.status === 'pending') {
            pendingTransactions++;
          }
          if (tx.transaction_type === 'refund') {
            refundCount++;
          }
        });
      }

      // Mock recent activity for demo
      const mockActivity: ActivityItem[] = [
        {
          id: '1',
          type: 'user_registration',
          description: 'New user registered: John Smith',
          timestamp: new Date(Date.now() - 1000 * 60 * 30).toISOString(),
          status: 'completed'
        },
        {
          id: '2',
          type: 'game_created',
          description: 'New game scheduled: Basketball Tournament',
          timestamp: new Date(Date.now() - 1000 * 60 * 60 * 2).toISOString(),
          status: 'pending'
        },
        {
          id: '3',
          type: 'venue_claimed',
          description: 'Venue claim request: Downtown Sports Center',
          timestamp: new Date(Date.now() - 1000 * 60 * 60 * 4).toISOString(),
          status: 'pending'
        }
      ];

      setStats({
        totalUsers: userCount || 0,
        activeGames: gameCount || 0,
        pendingApprovals: approvalCount || 0,
        totalVenues: venueCount || 0,
        recentActivity: mockActivity,
        totalRevenue,
        pendingTransactions,
        refundCount
      });
    } catch (error) {
      console.error('Error loading system stats:', error);
      toast.error('Failed to load system statistics');
    } finally {
      setLoading(false);
    }
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'user_registration': return <Users className="h-4 w-4" />;
      case 'game_created': return <Calendar className="h-4 w-4" />;
      case 'venue_claimed': return <Building className="h-4 w-4" />;
      default: return <AlertTriangle className="h-4 w-4" />;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'failed': return <XCircle className="h-4 w-4 text-red-500" />;
      default: return <Clock className="h-4 w-4 text-yellow-500" />;
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <MobileHeader />
        <div className="container mx-auto px-4 py-8 pt-20">
          <Card>
            <CardContent className="p-8 text-center">
              <p className="text-muted-foreground">Please log in to access the admin dashboard</p>
            </CardContent>
          </Card>
        </div>
        <MobileBottomNav />
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <MobileHeader />
      
      <div className="container mx-auto px-4 py-8 pt-20 pb-24 md:pb-8">
        <div className="space-y-6">
          <div>
            <h1 className="text-3xl font-bold">Admin Dashboard</h1>
            <p className="text-muted-foreground">
              System overview and management tools
            </p>
          </div>

          {/* System Overview Cards */}
          <div className="grid gap-4 md:grid-cols-4">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <Users className="h-8 w-8 text-primary" />
                  <div>
                    <p className="text-2xl font-bold">{loading ? '-' : stats.totalUsers}</p>
                    <p className="text-sm text-muted-foreground">Total Users</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <Calendar className="h-8 w-8 text-primary" />
                  <div>
                    <p className="text-2xl font-bold">{loading ? '-' : stats.activeGames}</p>
                    <p className="text-sm text-muted-foreground">Active Games</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <AlertTriangle className="h-8 w-8 text-yellow-500" />
                  <div>
                    <p className="text-2xl font-bold">{loading ? '-' : stats.pendingApprovals}</p>
                    <p className="text-sm text-muted-foreground">Pending Approvals</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <Building className="h-8 w-8 text-primary" />
                  <div>
                    <p className="text-2xl font-bold">{loading ? '-' : stats.totalVenues}</p>
                    <p className="text-sm text-muted-foreground">Total Venues</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Transaction Stats Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5" />
                Transaction Overview
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-3">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-sports-green/10 rounded-lg">
                    <TrendingUp className="h-6 w-6 text-sports-green" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">
                      {loading ? '-' : `$${stats.totalRevenue.toFixed(2)}`}
                    </p>
                    <p className="text-sm text-muted-foreground">Total Revenue</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-yellow-500/10 rounded-lg">
                    <Clock className="h-6 w-6 text-yellow-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{loading ? '-' : stats.pendingTransactions}</p>
                    <p className="text-sm text-muted-foreground">Pending Transactions</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-destructive/10 rounded-lg">
                    <XCircle className="h-6 w-6 text-destructive" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{loading ? '-' : stats.refundCount}</p>
                    <p className="text-sm text-muted-foreground">Refunds Processed</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Quick Access Cards */}
          <Card>
            <CardHeader>
              <CardTitle>Quick Access</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-4">
                <Button
                  variant="outline"
                  className="h-auto flex-col items-start p-4 hover:bg-primary/5"
                  onClick={() => navigate('/admin/users')}
                >
                  <Users className="h-6 w-6 mb-2 text-primary" />
                  <span className="font-semibold">User Management</span>
                  <span className="text-xs text-muted-foreground mt-1">Manage users and roles</span>
                </Button>

                <Button
                  variant="outline"
                  className="h-auto flex-col items-start p-4 hover:bg-primary/5"
                  onClick={() => navigate('/admin/transactions')}
                >
                  <DollarSign className="h-6 w-6 mb-2 text-primary" />
                  <span className="font-semibold">Transactions</span>
                  <span className="text-xs text-muted-foreground mt-1">View and manage payments</span>
                </Button>

                <Button
                  variant="outline"
                  className="h-auto flex-col items-start p-4 hover:bg-primary/5"
                  onClick={() => navigate('/admin/analytics')}
                >
                  <BarChart3 className="h-6 w-6 mb-2 text-primary" />
                  <span className="font-semibold">System Analytics</span>
                  <span className="text-xs text-muted-foreground mt-1">View platform metrics</span>
                </Button>

                <Button
                  variant="outline"
                  className="h-auto flex-col items-start p-4 hover:bg-primary/5"
                  onClick={() => navigate('/admin/platform-settings')}
                >
                  <Settings className="h-6 w-6 mb-2 text-primary" />
                  <span className="font-semibold">Settings</span>
                  <span className="text-xs text-muted-foreground mt-1">Configure platform</span>
                </Button>

                <Button
                  variant="outline"
                  className="h-auto flex-col items-start p-4 hover:bg-primary/5"
                  onClick={() => navigate('/admin/nominations')}
                >
                  <TrendingUp className="h-6 w-6 mb-2 text-primary" />
                  <span className="font-semibold">Nominations</span>
                  <span className="text-xs text-muted-foreground mt-1">Review player nominations</span>
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Management Tabs */}
          <Tabs defaultValue="overview" className="space-y-6">
            <TabsList className="grid w-full grid-cols-7">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="users">Users</TabsTrigger>
              <TabsTrigger value="rollover">Season Rollover</TabsTrigger>
              <TabsTrigger value="scheduled">Scheduled Rollovers</TabsTrigger>
              <TabsTrigger value="archives">Season Archives</TabsTrigger>
              <TabsTrigger value="compliance">Legal Compliance</TabsTrigger>
              <TabsTrigger value="content">Content</TabsTrigger>
              <TabsTrigger value="settings">Settings</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-6">
              {/* Recent Activity */}
              <Card>
                <CardHeader>
                  <CardTitle>Recent Activity</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {stats.recentActivity.map((activity) => (
                      <div key={activity.id} className="flex items-center justify-between p-3 border rounded-lg">
                        <div className="flex items-center gap-3">
                          {getActivityIcon(activity.type)}
                          <div>
                            <p className="font-medium">{activity.description}</p>
                            <p className="text-sm text-muted-foreground">
                              {new Date(activity.timestamp).toLocaleString()}
                            </p>
                          </div>
                        </div>
                        {getStatusIcon(activity.status)}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* System Health */}
              <Card>
                <CardHeader>
                  <CardTitle>System Health</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-4 md:grid-cols-3">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-green-500" />
                      <span>Database: Healthy</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-green-500" />
                      <span>API: Operational</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-green-500" />
                      <span>Storage: Available</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="users" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>User Management</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex gap-4">
                      <Button>View All Users</Button>
                      <Button variant="outline">Export Users</Button>
                      <Button variant="outline">Manage Roles</Button>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      User management features are available in demo mode.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="rollover" className="space-y-6">
              <SeasonRolloverWizard />
            </TabsContent>

            <TabsContent value="scheduled" className="space-y-6">
              <ScheduledRolloverManager />
            </TabsContent>

            <TabsContent value="archives" className="space-y-6">
              <SeasonArchiveViewer entityType="all" showRestoreOption={true} />
            </TabsContent>

            <TabsContent value="compliance" className="space-y-6">
              <Tabs defaultValue="dashboard" className="space-y-4">
                <TabsList>
                  <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
                  <TabsTrigger value="management">Policy Management</TabsTrigger>
                </TabsList>
                
                <TabsContent value="dashboard">
                  <ComplianceDashboard />
                </TabsContent>
                
                <TabsContent value="management">
                  <PolicyManagementAdmin />
                </TabsContent>
              </Tabs>
            </TabsContent>

            <TabsContent value="content" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Content Moderation</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex gap-4">
                      <Button>Review Reports</Button>
                      <Button variant="outline">Manage Games</Button>
                      <Button variant="outline">Venue Approvals</Button>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Content moderation tools are available in demo mode.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="settings" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>System Settings</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex gap-4">
                      <Button>System Configuration</Button>
                      <Button variant="outline">Email Settings</Button>
                      <Button variant="outline">Security Settings</Button>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      System settings can be configured here.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      <MobileBottomNav />
      <Footer />
    </div>
  );
}